import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SuccessModal from "../components/SuccessModal";
import {
  getPlans,
  createSubscription,
  verifyPayment,
  updateCurrency,
  getInvoices,
} from "../api/invoices";
import { getMe } from "../api/auth";

export default function Billing({ user }) {
  const navigate = useNavigate();

  /* =========================
      STATES
  ========================= */
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [userData, setUserData] = useState(user);
  const [currentPlan, setCurrentPlan] = useState(user?.plan || "free");
  const [currency, setCurrency] = useState(user?.currency || "INR");
  const [subscriptionStatus, setSubscriptionStatus] = useState("inactive");
  const [dbInvoiceCount, setDbInvoiceCount] = useState(0);
  const [successModal, setSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false); // Tracks active validation loops
  const [pendingPlan, setPendingPlan] = useState("");

  // Clean, targeted backend sync verification loop
  // useEffect(() => {
  //   let interval;

  //   if (isVerifying && currency === "USD") {
  //     interval = setInterval(async () => {
  //       try {
  //         const res = await getMe();
  //         const updatedUser = res.data.user;

  //         // Double check that the plan matches our expected purchase target
  //         if (
  //           updatedUser.subscription?.status === "active" &&
  //           updatedUser.plan === pendingPlan
  //         ) {
  //           setUserData(updatedUser);
  //           setCurrentPlan(updatedUser.plan);
  //           setSubscriptionStatus("active");
  //           setSuccessMessage(`${updatedUser.plan.toUpperCase()} plan activated successfully!`);
  //           setSuccessModal(true);
  //           setIsVerifying(false); // Stop the verification sequence
  //           clearInterval(interval);
  //         }
  //       } catch (err) {
  //         console.error("Verification loop check failed:", err);
  //       }
  //     }, 3000);
  //   }

  //   return () => {
  //     if (interval) clearInterval(interval);
  //   };
  // }, [isVerifying, currency, pendingPlan]);


  /* =========================
      LOAD DATA
  ========================= */
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
  let interval;

  if (isVerifying && currency === "USD") {
    interval = setInterval(async () => {
      try {
        console.log("Checking updated subscription...");

        const res = await getMe();
        const updatedUser = res.data.user;

        console.log("Updated User:", updatedUser);

        // ONLY CHECK PLAN
        if (updatedUser.plan === pendingPlan) {
          console.log("Plan updated successfully!");

          setUserData(updatedUser);

          setCurrentPlan(updatedUser.plan);

          setSubscriptionStatus(
            updatedUser.subscription?.status || "active"
          );

          setSuccessMessage(
            `${updatedUser.plan.toUpperCase()} plan activated successfully!`
          );

          setSuccessModal(true);

          setIsVerifying(false);

          clearInterval(interval);
        }
      } catch (err) {
        console.error("Verification loop failed:", err);
      }
    }, 2000);
  }

  return () => {
    if (interval) clearInterval(interval);
  };
}, [isVerifying, currency, pendingPlan]);
  const loadData = async () => {
    try {
      const [plansRes, userRes, invoicesRes] = await Promise.all([
        getPlans(),
        getMe(),
        getInvoices(),
      ]);

      const userDataFromApi = userRes.data.user;
      const plan = userDataFromApi.plan || "free";
      const statusFromDb = userDataFromApi.subscription?.status || "inactive";

      setPlans(plansRes.data.plans);
      setUserData(userDataFromApi);
      
      const count = invoicesRes.data.total || invoicesRes.data.length || 0;
      setDbInvoiceCount(count);

      setCurrentPlan(plan);
      setCurrency(userDataFromApi.currency || "INR");

      if (plan === "free") {
        setSubscriptionStatus("active");
      } else {
        setSubscriptionStatus(statusFromDb);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        return;
      }
      console.error("Billing load failed", err);
    }
  };

  /* =========================
     LIMITS & CALCULATIONS
  ========================= */
  const FREE_LIMIT = 5;
  const PLAN_LIMITS = {
    free: 5,
    starter: 50,
    pro: Infinity,
  };

  const totalLimit =
    currentPlan === "starter"
      ? FREE_LIMIT + PLAN_LIMITS.starter
      : PLAN_LIMITS[currentPlan];

  const actualUsedInvoices = dbInvoiceCount;

  const invoicesLeft =
    totalLimit === Infinity
      ? "Unlimited"
      : Math.max(totalLimit - actualUsedInvoices, 0);

  const usagePercent =
    totalLimit === Infinity
      ? 0
      : Math.min((actualUsedInvoices / totalLimit) * 100, 100);

  const circleRadius = 52;
  const circumference = 2 * Math.PI * circleRadius;
  const strokeDashoffset = circumference - (usagePercent / 100) * circumference;

  /* =========================
      CURRENCY TOGGLE
  ========================= */
  const handleCurrencyChange = async (curr) => {
    setCurrency(curr);
    try {
      await updateCurrency(curr);
    } catch (err) {
      if (err.response?.status === 401) {
        return;
      }
      console.error("Currency update failed", err);
    }
  };

  /* =========================
      PRICE CONVERTER
  ========================= */
  const convertPrice = (price) => {
    if (price === 0) return "Free";
    if (currency === "INR") return `₹${price}`;
    return `$${(price / 83).toFixed(2)}`;
  };

  /* =========================
      SUBSCRIBE FLOW
  ========================= */
  const handleSubscribe = async (planId) => {
    if (planId === "free" || planId === currentPlan) {
      return;
    }

    setLoading(planId);
    setPendingPlan(planId);

    try {
      const { data } = await createSubscription({
        plan: planId,
        currency,
      });

      // INR → Razorpay
      if (currency === "INR") {
        const options = {
          key: data.key,
          subscription_id: data.subscriptionId,
          name: "Invoice AI",
          description: `${planId.toUpperCase()} Plan`,
          handler: async (response) => {
            try {
              await verifyPayment({
                ...response,
                plan: planId,
              });

              const refreshedUser = await getMe();
              const updatedUser = refreshedUser.data.user;

              setUserData(updatedUser);
              setCurrentPlan(updatedUser.plan);
              setSubscriptionStatus(updatedUser.subscription?.status || "active");
              setSuccessMessage(`${planId.toUpperCase()} plan activated successfully!`);
              setSuccessModal(true);
            } catch (err) {
              alert(err.response?.data?.message || "Verification failed");
            }
          },
          prefill: {
            email: userData?.email,
            name: userData?.name,
          },
          theme: {
            color: "#2d2b69",
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
        return;
      }

      // USD → Paddle
      // if (currency === "USD") {
      //   window.Paddle.Environment.set("sandbox");
      //   window.Paddle.Initialize({
      //     token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
      //   });

      //   window.Paddle.Checkout.open({
      //     transactionId: data.transactionId,
      //     settings: {
      //       displayMode: "overlay",
      //       theme: "light",
      //     },
      //     // successCallback: () => {
      //     //   console.log("Paddle payment overlay completed successfully. Starting DB sync loops...");
      //     //   setIsVerifying(true); // Explicitly kick off database tracking loop here!
      //     // },
      //   });
      //     setIsVerifying(true);

      // }

      // USD → Paddle
if (currency === "USD") {
  window.Paddle.Environment.set("sandbox");

  window.Paddle.Initialize({
    token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
  });

  window.Paddle.Checkout.open({
    transactionId: data.transactionId,
    settings: {
      displayMode: "overlay",
      theme: "light",
    },
  });

  // Wait webhook update
  setTimeout(() => {
    setIsVerifying(true);
  }, 3000);
}
    } catch (err) {
      if (err.response?.status === 401) {
        return;
      }
      console.error(err);
      alert(err.response?.data?.message || "Payment failed");
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Sora', sans-serif; background: #f5f4f0; color: #1a1a24; }
        .page { max-width: 1100px; margin: auto; padding: 50px 20px; }
        .header { text-align: center; margin-bottom: 40px; }
        .title { font-size: 38px; font-weight: 700; margin-bottom: 10px; }
        .sub { color: #777; font-size: 15px; }
        .top-card { background: white; border-radius: 24px; padding: 30px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 35px; border: 1px solid #eee; flex-wrap: wrap; gap: 25px; }
        .plan-badge { display: inline-block; padding: 8px 16px; border-radius: 999px; background: #ede9fe; color: #5b21b6; font-size: 13px; font-weight: 600; margin-top: 12px; }
        .stats { display: flex; gap: 20px; margin-top: 20px; flex-wrap: wrap; }
        .stat-box { background: #fafafa; border: 1px solid #eee; padding: 16px; border-radius: 16px; min-width: 130px; }
        .stat-title { font-size: 12px; color: #777; margin-bottom: 5px; }
        .stat-value { font-size: 24px; font-weight: 700; }
        .usage-circle { position: relative; width: 140px; height: 140px; }
        .usage-circle svg { transform: rotate(-90deg); }
        .usage-text { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; flex-direction: column; }
        .usage-percent { font-size: 28px; font-weight: 700; }
        .usage-label { font-size: 12px; color: #777; }
        .currency-toggle { display: flex; justify-content: center; gap: 12px; margin-bottom: 35px; }
        .currency-toggle button { padding: 10px 18px; border-radius: 10px; border: 1px solid #ddd; background: white; cursor: pointer; font-weight: 600; }
        .currency-toggle button.active { background: #2d2b69; color: white; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .card { background: white; border-radius: 22px; padding: 28px; border: 1px solid #eee; transition: 0.25s; }
        .card:hover { transform: translateY(-4px); }
        .plan-name { font-size: 20px; font-weight: 700; }
        .price { font-size: 42px; font-weight: 700; margin: 18px 0; }
        .invoice-limit { color: #666; margin-bottom: 18px; }
        .features { list-style: none; margin-bottom: 25px; }
        .features li { margin-bottom: 10px; color: #555; font-size: 14px; }
        .btn { width: 100%; padding: 14px; border-radius: 14px; border: none; cursor: pointer; font-weight: 700; font-size: 15px; }
        .btn.primary { background: #2d2b69; color: white; }
        .btn.disabled { background: #e5e5e5; color: #666; cursor: not-allowed; }
        .back-btn { background: none; border: none; margin-bottom: 30px; cursor: pointer; font-weight: 600; color: #2d2b69; }
        @media(max-width: 900px) { .grid { grid-template-columns: 1fr; } .top-card { flex-direction: column; align-items: flex-start; } }
      `}</style>
      
      <SuccessModal
        open={successModal}
        title="Payment Successful"
        message={successMessage}
        buttonText="Go To Dashboard"
        onClose={() => {
          setSuccessModal(false);
          navigate("/dashboard");
        }}
      />
      
      <div className="page">
        <button className="back-btn" onClick={() => navigate("/dashboard")}>
          ← Back to Dashboard
        </button>

        <div className="header">
          <h1 className="title">Billing & Plans</h1>
          <p className="sub">Manage your subscription and invoice usage</p>
        </div>

        <div className="top-card">
          <div>
            <h2 style={{ fontSize: "28px", marginBottom: "6px" }}>Your Subscription</h2>
            <p style={{ color: "#777" }}>Monitor invoice usage and upgrade anytime</p>
            <div className="plan-badge">{currentPlan.toUpperCase()} PLAN</div>

            <div className="stats">
              <div className="stat-box">
                <div className="stat-title">Status</div>
                <div className="stat-value" style={{ 
                  color: subscriptionStatus === "active" ? "green" : "crimson", 
                  fontSize: "18px",
                  textTransform: "capitalize"
                }}>
                  {subscriptionStatus}
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-title">Used</div>
                <div className="stat-value">{actualUsedInvoices}</div>
              </div>
              <div className="stat-box">
                <div className="stat-title">Left</div>
                <div className="stat-value">{invoicesLeft}</div>
              </div>
            </div>
          </div>

          {currentPlan !== "pro" && (
            <div className="usage-circle">
              <svg width="140" height="140">
                <circle cx="70" cy="70" r={circleRadius} stroke="#eee" strokeWidth="10" fill="none" />
                <circle cx="70" cy="70" r={circleRadius} stroke="#2d2b69" strokeWidth="10" fill="none" 
                  strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" />
              </svg>
              <div className="usage-text">
                <div className="usage-percent">{Math.round(usagePercent)}%</div>
                <div className="usage-label">Used</div>
              </div>
            </div>
          )}

          {(currentPlan === "starter" || currentPlan === "pro") && (
            <div style={{ marginTop: "18px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <div style={{ padding: "14px 18px", background: "#fafafa", border: "1px solid #eee", borderRadius: "14px", minWidth: "180px" }}>
                <div style={{ fontSize: "12px", color: "#777", marginBottom: "4px" }}>Plan Started</div>
                <div style={{ fontSize: "16px", fontWeight: "600", color: "#1a1a24" }}>
                  {userData?.subscriptionDetails?.startDate
                    ? new Date(userData.subscriptionDetails.startDate).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
                    : "N/A"}
                </div>
              </div>

              <div style={{ padding: "14px 18px", background: "#fafafa", border: "1px solid #eee", borderRadius: "14px", minWidth: "180px" }}>
                <div style={{ fontSize: "12px", color: "#777", marginBottom: "4px" }}>Plan Expiry</div>
                <div style={{ fontSize: "16px", fontWeight: "600", color: "#1a1a24" }}>
                  {userData?.subscriptionDetails?.endDate
                    ? new Date(userData.subscriptionDetails.endDate).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
                    : "N/A"}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="currency-toggle">
          <button className={currency === "INR" ? "active" : ""} onClick={() => handleCurrencyChange("INR")}>
            INR (₹)
          </button>
          <button className={currency === "USD" ? "active" : ""} onClick={() => handleCurrencyChange("USD")}>
            USD ($)
          </button>
        </div>

        <div className="grid">
          {plans.map((plan) => {
            const isCurrent = plan.id === currentPlan;
            const isFree = plan.id === "free";
            const isFreeExhausted = dbInvoiceCount > 5;

            let buttonText = "Upgrade";
            let isDisabled = loading === plan.id || isVerifying;

            if (isCurrent) {
              buttonText = "Current Plan";
              isDisabled = true;
            } else if (isFree) {
              buttonText = isFreeExhausted ? "Free Plan Used" : "Active";
              isDisabled = true;
            } else if (currentPlan === "pro") {
              buttonText = "Downgrade";
              isDisabled = true;
            }

            return (
              <div key={plan.id} className="card">
                <div className="plan-name">{plan.name}</div>
                <div className="price">{convertPrice(plan.price)}</div>
                <div className="invoice-limit">{plan.invoices} invoices/month</div>
                <ul className="features">
                  {plan.features?.map((f, i) => <li key={i}>✔ {f}</li>)}
                </ul>

                <button
                  className={`btn ${isDisabled ? "disabled" : "primary"}`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isDisabled}
                >
                  {loading === plan.id ? "Processing..." : isVerifying && pendingPlan === plan.id ? "Verifying Plan..." : buttonText}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}